'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { categoriesData } from '@/data/categories';
import { CategoryCard } from './CategoryCard';
import { StatsBar } from './StatsBar';
import { CTAButton } from './CTAButton';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

const HEADING_WORDS = ['Explore', 'Our', 'Categories'];

export const CategoriesSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const eyebrowRef = useRef<HTMLSpanElement | null>(null);
  const wordsRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLParagraphElement | null>(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      if (eyebrowRef.current) {
        tl.fromTo(eyebrowRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' });
      }
      if (wordsRef.current) {
        tl.fromTo(
          wordsRef.current.children,
          { opacity: 0, y: 24, rotateX: -40 },
          { opacity: 1, y: 0, rotateX: 0, duration: 0.7, stagger: 0.08, ease: 'power3.out' },
          '-=0.2'
        );
      }
      if (lineRef.current) {
        tl.fromTo(lineRef.current, { width: 0 }, { width: 40, duration: 0.6, ease: 'power2.inOut' }, '-=0.3');
      }
      if (subRef.current) {
        tl.fromTo(subRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5 }, '-=0.3');
      }

      if (cardsRef.current) {
        gsap.fromTo(
          cardsRef.current.children,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      aria-label="Explore Our Categories"
      className="w-full bg-[#FEFDFB] py-14 sm:py-20 px-4 xs:px-6 flex justify-center overflow-hidden"
    >
      <div className="w-full max-w-[1400px]">
        {/* Section Header */}
        <div className="text-center mb-10 xs:mb-14" style={{ perspective: 600 }}>
          <span
            ref={eyebrowRef}
            className="inline-block text-[11px] tracking-[0.25em] uppercase text-[#B08D57] font-semibold mb-3"
          >
            Curated Materials
          </span>

          <div ref={wordsRef} className="flex items-center justify-center gap-x-3 flex-wrap">
            {HEADING_WORDS.map((word) => (
              <span
                key={word}
                className="inline-block text-2xl xs:text-3xl font-extrabold text-[#1F1F1F] tracking-wide uppercase"
              >
                {word}
              </span>
            ))}
          </div>

          <div ref={lineRef} className="h-px bg-[#B08D57] mx-auto mt-4 mb-4" style={{ width: 0 }} />

          <p ref={subRef} className="text-[13px] xs:text-[15px] text-[#84796B]">
            Premium quality products for every need
          </p>
        </div>

        {/* Card Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {categoriesData.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>

        <StatsBar />
        <CTAButton />
      </div>
    </section>
  );
};

export default CategoriesSection;